/**
 * The audit log viewer — a read-only window onto `audit_log`, which the database triggers write
 * (ADR 0003). Nothing here creates, edits or deletes a row.
 *
 * Org scope only: an `audit:read` holder sees the whole organisation's trail (RLS is the
 * boundary). This is distinct from `GET /payroll/reports/audit`, which is payroll-scoped and
 * paginated by page number; this one is keyset-paginated (newest first) over every audited table.
 *
 * `before` / `after` are the trigger's images: the changed columns only on UPDATE, the full row on
 * INSERT (`before` null) and DELETE (`after` null), with PII columns already replaced by a
 * `[redacted:…]` marker. The list returns them; the CSV export deliberately does not.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** Widest window one CSV export may cover. */
export const AUDIT_EXPORT_MAX_DAYS = 92;
/** Hard cap on rows in one CSV export. */
export const AUDIT_EXPORT_MAX_ROWS = 10_000;

/**
 * The audited tables people actually go looking for — the entity-type filter's suggestions. The
 * filter itself accepts any table name (free text), so a table audited later needs no change here.
 */
export const AUDIT_ENTITY_TYPES = [
  'people',
  'employments',
  'employment_assignments',
  'compensations',
  'bank_accounts',
  'credentials',
  'role_grants',
  'departments',
  'locations',
  'legal_entities',
  'workflow_definitions',
  'company_policies',
  'leave_balance_adjustments',
  'projects',
] as const;

const entityTypeFilter = z
  .string()
  .max(100)
  .regex(/^[a-z0-9_]+$/, 'Entity type is a table name, e.g. employments');

/** A dotted prefix such as `roles.` or a full action such as `people.update`. */
const actionPrefixFilter = z
  .string()
  .max(100)
  .regex(/^[a-z0-9_.]+$/, 'Action looks like people.update; a prefix such as roles. also works');

const filterFields = {
  actorPersonId: uuidSchema.optional(),
  entityType: entityTypeFilter.optional(),
  entityId: z.string().trim().min(1).max(100).optional(),
  action: actionPrefixFilter.optional(),
};

export const auditLogListQuerySchema = z.object({
  from: instantSchema.optional(),
  to: instantSchema.optional(),
  ...filterFields,
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Opaque — the `nextCursor` of the previous page, passed back untouched. */
  cursor: z.string().min(1).max(200).optional(),
});
export type AuditLogListQuery = z.infer<typeof auditLogListQuerySchema>;

export const auditLogExportQuerySchema = z.object({
  from: instantSchema,
  to: instantSchema,
  ...filterFields,
});
export type AuditLogExportQuery = z.infer<typeof auditLogExportQuerySchema>;

export const auditLogPersonSchema = z.object({
  personId: uuidSchema,
  displayName: z.string(),
});
export type AuditLogPerson = z.infer<typeof auditLogPersonSchema>;

export const auditLogEntrySchema = z.object({
  id: uuidSchema,
  createdAt: instantSchema,
  /** Null for system jobs, and for an actor whose person record no longer resolves. */
  actor: auditLogPersonSchema.nullable(),
  actorType: z.string(),
  impersonator: auditLogPersonSchema.nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  requestId: z.string().nullable(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

export const auditLogListSchema = z.object({
  items: z.array(auditLogEntrySchema),
  /** Null on the last page. */
  nextCursor: z.string().nullable(),
});
export type AuditLogList = z.infer<typeof auditLogListSchema>;

/** Inline, like the payroll registers and the salary-query SLA report — nothing is stored. */
export const auditLogExportSchema = z.object({
  fileName: z.string(),
  contentType: z.string(),
  content: z.string(),
  rowCount: z.number().int().nonnegative(),
  /** True when the row cap cut the result short — narrow the filters and export again. */
  truncated: z.boolean(),
  generatedAt: instantSchema,
});
export type AuditLogExport = z.infer<typeof auditLogExportSchema>;

export const auditLogContract = c.router({
  // Declared before the list so nothing can ever shadow it (see contract-route-order.test.ts).
  exportAuditLogs: {
    method: 'GET',
    path: '/audit-logs/export',
    query: auditLogExportQuerySchema,
    responses: {
      200: auditLogExportSchema,
      422: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'CSV of the audit trail for a date range (no before/after values)',
    metadata: routeMeta('audit:export'),
  },
  listAuditLogs: {
    method: 'GET',
    path: '/audit-logs',
    query: auditLogListQuerySchema,
    responses: {
      200: auditLogListSchema,
      422: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'The organisation audit trail, newest first, keyset-paginated',
    metadata: routeMeta('audit:read'),
  },
});
